import { AUTO_STYLE, keyframes, style } from '@angular/animations';
import { Directive, ElementRef, Input, OnInit } from '@angular/core';
import { Util } from '@libs/utilities/util';

import { NgTimedAnimationService } from '../../shared/services/timed-animation.service';
import { EnumNgEventName } from '../../shared/event-animation.data';
import { INgAnimationEffectOptions, INgAnimationStates } from '../../shared/event-animation.interface';


/**------------------------------------------------------
 * Animation State Definitions
 */
function buzzOut(translateX: number | undefined, rotate: number | undefined): INgAnimationStates {

	//0 - set the default if no specific value is given
	translateX = translateX ? translateX : 3;
	rotate 	   = rotate 	? rotate 	 : 2;

	//1 - make transition steps by number of transition state steps
	const translateDifference 	: number = Util.Number.roundDouble(translateX / 10);  					// divide the translate value by total number of transition states
	const translateX1 			: number = Util.Number.roundDouble(translateX - translateDifference);
	const translateX2 			: number = Util.Number.roundDouble(translateX1 - translateDifference);
	const translateX3 			: number = Util.Number.roundDouble(translateX2 - translateDifference);
	const translateX4 			: number = Util.Number.roundDouble(translateX3 - translateDifference);
	const translateX5 			: number = Util.Number.roundDouble(translateX4 - translateDifference);
	const translateX6 			: number = Util.Number.roundDouble(translateX5 - translateDifference);
	const translateX7 			: number = Util.Number.roundDouble(translateX6 - translateDifference);
	const translateX8 			: number = Util.Number.roundDouble(translateX7 - translateDifference);
	const translateX9 			: number = Util.Number.roundDouble(translateX8 - translateDifference);
	const translateX10 			: number = Util.Number.roundDouble(translateX9 - translateDifference);

	const rotateDifference 		: number = Util.Number.roundDouble(rotate / 10); 						// divide the translate value by total number of transition states
	const rotate1 				: number = Util.Number.roundDouble(rotate - rotateDifference);
	const rotate2 				: number = Util.Number.roundDouble(rotate1 - rotateDifference);
	const rotate3 				: number = Util.Number.roundDouble(rotate2 - rotateDifference);
	const rotate4 				: number = Util.Number.roundDouble(rotate3 - rotateDifference);
	const rotate5 				: number = Util.Number.roundDouble(rotate4 - rotateDifference);
	const rotate6 				: number = Util.Number.roundDouble(rotate5 - rotateDifference);
	const rotate7 				: number = Util.Number.roundDouble(rotate6 - rotateDifference);
	const rotate8 				: number = Util.Number.roundDouble(rotate7 - rotateDifference);
	const rotate9 				: number = Util.Number.roundDouble(rotate8 - rotateDifference);
	const rotate10 				: number = Util.Number.roundDouble(rotate9 - rotateDifference);

	//2 - create the animations state
	const animationStates: INgAnimationStates = {
		start 	: keyframes([
			style({
				visibility 	: AUTO_STYLE,
				transform 	: 'translateX(0) rotate(0)',
				easing 		: 'ease',
				offset 		: 0
			}),
			style({
				transform 	: `translateX(-${Util.Number.toPositive(translateX1)}px) rotate(-${Util.Number.toPositive(rotate1)}deg)`,
				easing 		: 'ease',
				offset 		: 0.1
			}),
			style({
				transform 	: `translateX(${Util.Number.toPositive(translateX2)}px) rotate(${Util.Number.toPositive(rotate2)}deg)`,
				easing 		: 'ease',
				offset 		: 0.2
			}),
			style({
				transform 	: `translateX(-${Util.Number.toPositive(translateX3)}px) rotate(-${Util.Number.toPositive(rotate3)}deg)`,
				easing 		: 'ease',
				offset 		: 0.3
			}),
			style({
				transform 	: `translateX(${Util.Number.toPositive(translateX4)}px) rotate(${Util.Number.toPositive(rotate4)}deg)`,
				easing 		: 'ease',
				offset 		: 0.4
			}),
			style({
				transform 	: `translateX(-${Util.Number.toPositive(translateX5)}px) rotate(-${Util.Number.toPositive(rotate5)}deg)`,
				easing 		: 'ease',
				offset 		: 0.5
			}),
			style({
				transform 	: `translateX(${Util.Number.toPositive(translateX6)}px) rotate(${Util.Number.toPositive(rotate6)}deg)`,
				easing 		: 'ease',
				offset 		: 0.6
			}),
			style({
				transform 	: `translateX(-${Util.Number.toPositive(translateX7)}px) rotate(-${Util.Number.toPositive(rotate7)}deg)`,
				easing 		: 'ease',
				offset 		: 0.7
			}),
			style({
				transform 	: `translateX(${Util.Number.toPositive(translateX8)}px) rotate(${Util.Number.toPositive(rotate8)}deg)`,
				easing 		: 'ease',
				offset 		: 0.8
			}),
			style({
				transform 	: `translateX(-${Util.Number.toPositive(translateX9)}px) rotate(-${Util.Number.toPositive(rotate9)}deg)`,
				easing 		: 'ease',
				offset 		: 0.9
			}),
			style({
				transform 	: `translateX(${Util.Number.toPositive(translateX10)}px) rotate(${Util.Number.toPositive(rotate10)}deg)`,
				easing 		: 'ease',
				offset 		: 1
			})
		])
	};
	return animationStates;
}


/**------------------------------------------------------
 * BuzzOut on Event
 */
@Directive({
    selector: '[ngBuzzOutOnEvent]',
    providers: [NgTimedAnimationService],
    standalone: false
})
export class NgBuzzOutOnEventDirective implements OnInit {

	//** Input Decorators */
	@Input() effectOptions: Partial<INgCustomEffectOptions> = null!;
	@Input() targetElement: HTMLElement			 		  = null!;

	constructor(
		private elementRef			  : ElementRef,
		private timedAnimationService : NgTimedAnimationService
	) {}

	//** Setup the Animation */
	ngOnInit(): void {

		//0 - check the translate and rotate values are relative to the animation functionality
		if (this.effectOptions?.translateX && this.effectOptions?.translateX < 0) throw new Error(`BuzzOutOnEventDirective => ngOnInit => FATAL ERROR: Provided translateX value should be positive number: ${this.effectOptions?.translateX}`);
		if (this.effectOptions?.rotate     && this.effectOptions?.rotate 	 < 0) throw new Error(`BuzzOutOnEventDirective => ngOnInit => FATAL ERROR: Provided rotate value should be positive number: ${this.effectOptions?.rotate}`);

		//1 - setup the animation
		this.timedAnimationService.setupAnimation({
			triggerElement	: this.elementRef.nativeElement,
			targetElement	: this.targetElement,
			states			: buzzOut(this.effectOptions?.translateX, this.effectOptions?.rotate),
			event			: this.effectOptions?.event!,
			timing			: {
				duration	: this.effectOptions?.duration,
				delay		: this.effectOptions?.delay,
				timeout		: this.effectOptions?.timeout || this.effectOptions?.duration
			}
		});
	}
}


/**------------------------------------------------------
 * BuzzOut on Mouse Hover
 */
@Directive({
    selector: '[ngBuzzOutOnMouseHover]',
    providers: [NgTimedAnimationService],
    standalone: false
})
export class NgBuzzOutOnMouseHoverDirective implements OnInit {

	//** Input Decorators */
	@Input() effectOptions: Partial<INgCustomEffectOptions> = null!;
	@Input() targetElement: HTMLElement = null!;

	constructor(
		private elementRef 				: ElementRef,
		private timedAnimationService 	: NgTimedAnimationService
	) {}

	//** Setup the Animation */
	ngOnInit(): void {

		//0 - check the translate and rotate values are relative to the animation functionality
		if (this.effectOptions?.translateX && this.effectOptions?.translateX < 0) throw new Error(`BuzzOutOnMouseHoverDirective => ngOnInit => FATAL ERROR: Provided translateX value should be positive number: ${this.effectOptions?.translateX}`);
		if (this.effectOptions?.rotate 	   && this.effectOptions?.rotate 	 < 0) throw new Error(`BuzzOutOnMouseHoverDirective => ngOnInit => FATAL ERROR: Provided rotate value should be positive number: ${this.effectOptions?.rotate}`);

		//1 - setup the animation
		this.timedAnimationService.setupAnimation({
			triggerElement	: this.elementRef.nativeElement,
			targetElement	: this.targetElement,
			states			: buzzOut(this.effectOptions?.translateX, this.effectOptions?.rotate),
			event			: EnumNgEventName.MouseHover,
			timing			: {
				duration	: this.effectOptions?.duration,
				delay		: this.effectOptions?.delay,
				timeout		: this.effectOptions?.timeout || this.effectOptions?.duration
			}
		});
	}
}


//** Interfaces --------------------------------- */
interface INgCustomEffectOptions extends INgAnimationEffectOptions {
	translateX 	: number;
	rotate 		: number;
}
